import { Avatar, Button, message, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import type { FileRecord } from "@/common/define";
import TableConfiguration, {
  type ColumnConfig,
} from "@/features/drawings/components/TableConfiguration";
import ResizableColumnHeader from "@/components/ResizableColumn/ResizableColumnHeader";
import { ConversionStatusCell } from "@/features/file-management/pages/FilePage/components/ConversionStatusCell";

const { Text } = Typography;

export interface ColumnOptions {
  highlightText?: (text: string, searchValue: string) => React.ReactNode;
  searchValue?: string;
  onClickVersion?: (name: string) => void;
  openInfoPanels: (records: FileRecord[]) => void;
  configModalVisible: boolean;
  setConfigModalVisible: (visible: boolean) => void;
  columnConfig: ColumnConfig[];
  handleConfigChange: (newColumns: ColumnConfig[]) => void;
  getDraggableHeaderCell: (column: any) => any;
  columnWidths: { [key: string]: number };
  onColumnResize: (columnKey: string, width: number) => void;
  t: (key: string) => string;
}

export const createFilesTableColumns = (
  options: ColumnOptions,
): ColumnsType<FileRecord> => {
  const {
    highlightText,
    searchValue,
    onClickVersion,
    openInfoPanels,
    configModalVisible,
    setConfigModalVisible,
    columnConfig,
    handleConfigChange,
    getDraggableHeaderCell,
    columnWidths,
    onColumnResize,
    t,
  } = options;

  // Helper to get column width
  const getWidth = (key: string, defaultWidth: number) => {
    return columnWidths[key] || defaultWidth;
  };

  // Helper to create resizable header
  const createResizableHeader = (key: string, defaultWidth: number) => ({
    onHeaderCell: (column: any) => ({
      ...getDraggableHeaderCell(column),
      width: getWidth(key, defaultWidth),
      onResize: (
        e: React.SyntheticEvent,
        { size }: { size: { width: number } },
      ) => {
        e.stopPropagation();
        onColumnResize(key, size.width);
      },
    }),
    onCell: () => ({
      style: {
        maxWidth: getWidth(key, defaultWidth),
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }),
  });

  return [
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("name", 200)}
          onResize={(width) => onColumnResize("name", width)}
        >
          <span
            style={{
              fontSize: 15,
              letterSpacing: "1px",
              textShadow: "0 1px 2px #eee",
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("file_name")}
          </span>
        </ResizableColumnHeader>
      ),
      dataIndex: "name",
      key: "name",
      width: getWidth("name", 200),
      ...createResizableHeader("name", 200),
      render: (
        text: string,
        record: FileRecord & {
          isGroupHeader?: boolean;
          groupValue?: string;
          groupCount?: number;
        },
      ) => {
        if (record.isGroupHeader) {
          return (
            <Tooltip
              title={`${record.groupValue} (${record.groupCount})`}
              placement="rightTop"
            >
              <Tag
                style={{
                  borderRadius: 8,
                  fontSize: 15,
                  padding: "4px 12px",
                }}
              >
                {record.groupValue} ({record.groupCount})
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={text} placement="rightTop">
            <div
              style={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                borderRadius: 8,
                padding: "6px 10px",
                transition: "background 0.2s",
                fontSize: 15,
              }}
              onClick={() => {
                options.openInfoPanels([record]);
              }}
            >
              {options.highlightText
                ? options.highlightText(text, options.searchValue ?? "")
                : text}
            </div>
          </Tooltip>
        );
      },
    },

    {
      title: (
        <ResizableColumnHeader
          width={getWidth("format", 80)}
          onResize={(width) => onColumnResize("format", width)}
        >
          <span
            style={{
              fontSize: 15,
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("format")}
          </span>
        </ResizableColumnHeader>
      ),
      dataIndex: "format",
      key: "format",
      width: getWidth("format", 80),
      ...createResizableHeader("format", 80),
      render: (type: string, _record: FileRecord) => (
        <Tooltip title={type} placement="rightTop">
          <Tag
            color="#91D5FF"
            style={{
              borderRadius: 8,
              fontSize: 15,
              padding: "",
              background: "#E6F7FF",
              color: "#1890FF",
              border: "1px solid #91D5FF",
            }}
          >
            {type}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("format", 60)}
          onResize={(width) => onColumnResize("format", width)}
        >
          <span
            style={{
              fontSize: 15,
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("version")}
          </span>
        </ResizableColumnHeader>
      ),
      dataIndex: "version",
      key: "version",
      width: getWidth("version", 60),
      ...createResizableHeader("version", 60),
      render: (_: any, record: any) => {
        const hasVersion = !!record.version;
        const isClickable = hasVersion && !!options.onClickVersion;

        const handleClick = () => {
          if (!hasVersion) {
            message.info(t("no_version_history"));
            return;
          }

          options.onClickVersion?.(record);
        };

        return (
          <Tag
            onClick={handleClick}
            style={{
              cursor: isClickable ? "pointer" : "default",
              borderRadius: 8,
              fontSize: 15,
              background: "#E6F7FF",
              color: "#1890FF",
              border: "1px solid #91D5FF",
              opacity: isClickable ? 1 : 0.5,
            }}
          >
            {record.version || "-"}
          </Tag>
        );
      },
    },
    {
      title: (
        <span
          style={{
            fontSize: 15,
            padding: "5px 0",
            display: "inline-block",
          }}
        >
          {t("system")}
        </span>
      ),
      dataIndex: "system",
      key: "system",
      width: getWidth("system", 120),
      ...createResizableHeader("system", 120),
      render: (system: string, record: any) => {
        if (record.isGroupHeader) return null;
        return <span style={{  }}>{system}</span>;
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("owner", 150)}
          onResize={(width) => onColumnResize("owner", width)}
        >
          <span
            style={{
              fontSize: 15,
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("owner")}
          </span>
          {/* {t("owner")} */}
        </ResizableColumnHeader>
      ),
      dataIndex: "owner",
      key: "owner",
      width: getWidth("owner", 150),
      ...createResizableHeader("owner", 150),
      render: (name: string, record: any) => {
        if (record.isGroupHeader) return null;
        return (
          <Space>
            <Text style={{ fontSize: 15 }}>
              {record.owner?.name || name}
            </Text>
          </Space>
        );
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("lastUploaded", 160)}
          onResize={(width) => onColumnResize("lastUploaded", width)}
        >
          <span
            style={{
              fontSize: 15,
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("last_uploaded")}
          </span>
          {/* {t("last_uploaded")} */}
        </ResizableColumnHeader>
      ),
      dataIndex: "lastUploaded",
      key: "lastUploaded",
      width: getWidth("lastUploaded", 160),
      ...createResizableHeader("lastUploaded", 160),
      render: (lastUploaded: string, record: any) => {
        if (record.isGroupHeader) return null;
        return <span style={{ }}>{lastUploaded}</span>;
        // ...existing code...
        // Make text color consistent
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("size", 100)}
          onResize={(width) => onColumnResize("size", width)}
        >
          <span
            style={{
              fontSize: 15,
              padding: "5px 0",
              display: "inline-block",
            }}
          >
            {t("size")}
          </span>
          {/* {t("size")} */}
        </ResizableColumnHeader>
      ),
      dataIndex: "size",
      key: "size",
      width: getWidth("size", 100),
      ...createResizableHeader("size", 100),
      render: (size: number, record: FileRecord) => {
        return (
          <span style={{  }}>
            {size || "-"}
          </span>
        );
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("status", 100)}
          onResize={(width) => onColumnResize("status", width)}
        >
          {t("status")}
        </ResizableColumnHeader>
      ),
      dataIndex: "status",
      key: "status",
      width: getWidth("status", 100),
      ...createResizableHeader("status", 100),
      render: (status: string, _record: FileRecord) => (
        <Tag
          color="#ffe082"
          style={{
            borderRadius: 8,
            fontSize: 15,
            padding: "2px 10px",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("conversionStatus", 130)}
          onResize={(width) => onColumnResize("conversionStatus", width)}
        >
          {t("convert_status")}
        </ResizableColumnHeader>
      ),
      dataIndex: "fileId",
      key: "conversionStatus",
      width: getWidth("conversionStatus", 130),
      ...createResizableHeader("conversionStatus", 130),
      render: (_: string, record: any) => {
        if (record.isGroupHeader) return null;
        return (
          <ConversionStatusCell
            baseFileId={record.fileId ? String(record.fileId) : undefined}
            format={record.format}
            version={record.version}
            fileName={record.name}
          />
        );
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("discipline", 100)}
          onResize={(width) => onColumnResize("discipline", width)}
        >
          {t("discipline")}
        </ResizableColumnHeader>
      ),
      dataIndex: "discipline",
      key: "discipline",
      width: getWidth("discipline", 100),
      ...createResizableHeader("discipline", 100),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("type", 80)}
          onResize={(width) => onColumnResize("type", width)}
        >
          {t("type")}
        </ResizableColumnHeader>
      ),
      dataIndex: "type",
      key: "type",
      width: getWidth("type", 80),
      ...createResizableHeader("type", 80),
      render: (type: string, record: any) => {
        if (record.isGroupHeader) return null;
        const shortType =
          type && type.length > 3 ? type.slice(0, 3) + "..." : type;
        return (
          <Tooltip title={type} placement="topLeft">
            <span style={{  }}>
              {shortType}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("originator", 120)}
          onResize={(width) => onColumnResize("originator", width)}
        >
          {t("originator")}
        </ResizableColumnHeader>
      ),
      dataIndex: "originator",
      key: "originator",
      width: getWidth("originator", 120),
      ...createResizableHeader("originator", 120),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("location", 120)}
          onResize={(width) => onColumnResize("location", width)}
        >
          {t("location")}
        </ResizableColumnHeader>
      ),
      dataIndex: "location",
      key: "location",
      width: getWidth("location", 120),
      ...createResizableHeader("location", 120),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("author", 100)}
          onResize={(width) => onColumnResize("author", width)}
        >
          {t("author")}
        </ResizableColumnHeader>
      ),
      dataIndex: "author",
      key: "author",
      width: getWidth("author", 100),
      ...createResizableHeader("author", 100),
    },
    {
      title: (
        <ResizableColumnHeader
          width={getWidth("createdDate", 100)}
          onResize={(width) => onColumnResize("createdDate", width)}
        >
          {t("created_date")}
        </ResizableColumnHeader>
      ),
      dataIndex: "createdDate",
      key: "createdDate",
      width: getWidth("createdDate", 100),
      ...createResizableHeader("createdDate", 100),
    },
    // {
    //   title: (
    //     <TableConfiguration
    //       columns={columnConfig}
    //       onConfigChange={handleConfigChange}
    //     />
    //   ),
    //   key: "actions",
    //   width: 40,
    //   fixed: "right",
    //   align: "right",
    //   render: () => null,
    // },
  ];
};

export const fileColumns = [
  { key: "name", title: "File Name", visible: true },
  { key: "format", title: "Format", visible: true },
  { key: "version", title: "Version", visible: true },
  { key: "conversionStatus", title: "Convert Status", visible: true },
  { key: "discipline", title: "Discipline", visible: false },
  { key: "versionUploadedBy", title: "Version uploaded by", visible: true },
  { key: "owner", title: "Owner", visible: true },
  { key: "lastUploaded", title: "Last uploaded", visible: true },
  { key: "size", title: "Size", visible: true },
  { key: "location", title: "Location", visible: false },
  { key: "type", title: "Type", visible: false },
  { key: "system", title: "System", visible: false },
  { key: "originator", title: "Originator", visible: false },
  { key: "author", title: "Author", visible: false },
  { key: "createdDate", title: "Created Date", visible: false },
  { key: "status", title: "Status", visible: false },
  { key: "actions", title: "", visible: true, fixed: true },
];

export const defaultFileColumnWidths = {
  name: 200,
  format: 80,
  version: 60,
  system: 120,
  versionUploadedBy: 150,
  owner: 150,
  lastUploaded: 160,
  size: 100,
  status: 100,
  conversionStatus: 130,
  discipline: 100,
  type: 80,
  originator: 120,
  location: 120,
  author: 100,
  createdDate: 100,
};
